import { useEffect, useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import ReviewForm from "@/components/ReviewForm";
import { Star, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ n }) {
    return (
        <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3D2318]/15"}`} />)}
        </span>
    );
}

export default function Testimonials() {
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    }, []);

    const all = [
        ...reviews.map((r) => ({ ...r, source: "Website", key: r.id })),
        ...TESTIMONIALS.map((t) => ({ ...t, rating: 5, key: t.name })),
    ];

    return (
        <section id="testimonials" data-testid="testimonials-section" className="py-16 md:py-24 lg:py-32 bg-[#3D2318] text-[#FFFDF9] overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-4 lg:sticky lg:top-28">
                        <Reveal>
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Happy Words</p>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                                From Our <span className="font-script font-medium text-[#F8D7E3] text-4xl sm:text-5xl lg:text-6xl align-middle">Customers</span>
                            </h2>
                            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#FFFDF9]/5 px-5 py-2.5" data-testid="testimonials-rating">
                                <span className="flex gap-0.5" aria-hidden="true">
                                    {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />)}
                                </span>
                                <span className="text-sm font-semibold text-[#F8D7E3]">5.0 rated on Facebook</span>
                            </div>
                            <p className="mt-6 text-sm sm:text-base text-[#FFFDF9]/70 leading-relaxed font-light">
                                Every order is a chance to be part of someone's celebration — here's what a few of them had to say.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-8"><ReviewForm /></div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-8 grid md:grid-cols-2 gap-6 items-start" data-testid="testimonials-list">
                        {all.map((t, i) => (
                            <Reveal key={t.key} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-10" : ""}>
                                <figure className={`relative rounded-[2rem] bg-[#FFFDF9] p-8 sm:p-9 text-[#3D2318] shadow-[0_30px_70px_rgba(0,0,0,0.25)] ${i % 2 === 0 ? "rotate-[0.6deg]" : "-rotate-[0.6deg]"}`} data-testid={`testimonial-${i}`}>
                                    <Quote className="absolute -top-5 left-8 h-10 w-10 fill-[#C95B7D] text-[#C95B7D]" aria-hidden="true" />
                                    <Stars n={t.rating} />
                                    <blockquote className="mt-3 font-display text-base sm:text-lg lg:text-xl leading-relaxed">“{t.quote}”</blockquote>
                                    <figcaption className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#F2E1E6] pt-5">
                                        <div>
                                            <p className="text-sm font-semibold text-[#3D2318]" data-testid={`testimonial-${i}-name`}>{t.name}</p>
                                            {t.occasion && <p className="text-xs text-[#8C6A5D]">{t.occasion}</p>}
                                        </div>
                                        <span className="rounded-full bg-[#FDF0F4] border border-[#F2E1E6] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#C95B7D]">via {t.source}</span>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
